import { useEffect, useState, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import api, { API } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { Button } from "../../components/ui/button";
import { Progress } from "../../components/ui/progress";
import { Checkbox } from "../../components/ui/checkbox";
import { ChevronLeft, ChevronRight, Upload, FileText, Trash2, Download, CheckCircle2, Save, GraduationCap } from "lucide-react";
import * as OPT from "../../lib/options";
import { Field, TextField, AreaField, SelectField, YesNo, CheckGroup } from "./FormControls";

const STEPS = ["Personal", "Study & Availability", "Short Answers", "CV", "Review"];

export default function InternApplicationWizard({ job }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [step, setStep] = useState(0);
  const [profile, setProfile] = useState({});
  const [answers, setAnswers] = useState({});
  const [appId, setAppId] = useState(null);
  const [cv, setCv] = useState(null);
  const [consent, setConsent] = useState(false);
  const [futureConsent, setFutureConsent] = useState(false);
  const [saving, setSaving] = useState(false);
  const [submitted, setSubmitted] = useState(null);

  useEffect(() => {
    (async () => {
      const [p, a, c] = await Promise.all([
        api.get("/candidate/profile"),
        api.post("/candidate/applications", { job_id: job?.id || null }),
        api.get("/candidate/cv"),
      ]);
      const prof = p.data || {};
      if (!prof.first_name && user) prof.email = prof.email || user.email;
      if (!prof.county) prof.county = "Dublin";
      setProfile(prof);
      setAppId(a.data.id);
      setAnswers(a.data.answers || {});
      if (a.data.status && a.data.status !== "draft") setSubmitted({ reference: a.data.reference });
      if (c.data && c.data.id) setCv(c.data);
    })().catch(() => toast.error("Could not load your application"));
  }, [user, job]);

  const upd = (k, v) => setProfile((p) => ({ ...p, [k]: v }));
  const updAns = (k, v) => setAnswers((a) => ({ ...a, [k]: v }));

  const save = useCallback(async (silent) => {
    setSaving(true);
    try {
      await api.put("/candidate/profile", profile);
      if (appId) await api.put(`/candidate/applications/${appId}`, { answers });
      if (!silent) toast.success("Draft saved");
    } catch { if (!silent) toast.error("Save failed"); }
    finally { setSaving(false); }
  }, [profile, answers, appId]);

  const validateStep = () => {
    if (step === 0 && (!profile.first_name?.trim() || !profile.last_name?.trim())) { toast.error("Please enter your first and last name"); return false; }
    if (step === 1 && (!profile.currently_studying || !profile.right_to_work || !profile.institution?.trim() || !profile.course?.trim())) { toast.error("Please complete your study details and work eligibility"); return false; }
    if (step === 2 && !(answers.q_why || "").trim()) { toast.error("Please tell us why you'd like to build your career with KERA"); return false; }
    return true;
  };
  const next = async () => { if (!validateStep()) return; await save(true); setStep((s) => Math.min(s + 1, STEPS.length - 1)); window.scrollTo(0, 0); };
  const prev = () => { setStep((s) => Math.max(s - 1, 0)); window.scrollTo(0, 0); };

  const submit = async () => {
    if (!consent) return toast.error("Please provide privacy consent to submit");
    try {
      await save(true);
      const { data } = await api.post(`/candidate/applications/${appId}/submit`, { consent, future_consent: futureConsent });
      setSubmitted({ reference: data.reference });
      window.scrollTo(0, 0);
    } catch (e) { toast.error(e.response?.data?.detail || "Submission failed"); }
  };

  const uploadCv = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    toast.loading("Uploading & parsing CV…", { id: "cv" });
    try {
      const { data } = await api.post("/candidate/cv", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setCv(data);
      toast.success("CV uploaded", { id: "cv" });
    } catch (e) { toast.error(e.response?.data?.detail || "Upload failed", { id: "cv" }); }
  };
  const deleteCv = async () => { await api.delete("/candidate/cv"); setCv(null); toast.success("CV removed"); };

  if (submitted) {
    return (
      <div className="min-h-screen grid place-items-center bg-[#101820] p-6">
        <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
          className="bg-white rounded-2xl p-10 max-w-lg text-center border border-[#C79A3B]/40">
          <div className="w-16 h-16 rounded-full bg-[#2E7D32]/10 grid place-items-center mx-auto mb-5"><CheckCircle2 className="text-[#2E7D32] h-8 w-8" /></div>
          <h1 className="font-heading text-2xl font-extrabold text-[#101820]">Application submitted successfully</h1>
          <p className="mt-3 text-muted-foreground">Thank you for applying to the KERA internship. Your application reference is:</p>
          <div data-testid="intern-submitted-reference" className="mt-4 font-mono-data text-lg font-bold text-[#8a6a1e] bg-[#C79A3B]/10 border border-[#C79A3B]/30 rounded-lg py-3">{submitted.reference}</div>
          <Button data-testid="go-dashboard-button" onClick={() => navigate("/dashboard")} className="mt-7 bg-[#101820] text-[#FAF6EE] rounded-full px-7">Go to my dashboard</Button>
        </motion.div>
      </div>
    );
  }

  const pct = Math.round(((step + 1) / STEPS.length) * 100);

  return (
    <div className="min-h-screen bg-[#F7F0E2]">
      <header className="bg-[#101820] text-[#FAF6EE] sticky top-0 z-40 border-b border-[#C79A3B]/25">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/dashboard" className="flex items-center gap-2">
            <img src="/kera-logo.webp" alt="KERA" className="h-8 w-auto" />
            <span className="font-heading font-bold hidden sm:inline">{job?.title || "Sales & Marketing Intern"} · Application</span>
          </Link>
          <Button data-testid="save-draft-button" variant="ghost" onClick={() => save(false)} disabled={saving}
            className="text-[#C79A3B] hover:text-[#D4AF57] hover:bg-white/5">
            <Save className="h-4 w-4 mr-1" /> {saving ? "Saving…" : "Save draft"}
          </Button>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="mb-6">
          <div className="flex justify-between text-xs text-muted-foreground mb-2">
            <span>Step {step + 1} of {STEPS.length} · {STEPS[step]}</span><span>{pct}% complete</span>
          </div>
          <Progress data-testid="wizard-step-progress-bar" value={pct} className="h-2" />
          <div className="hidden md:flex justify-between mt-3">
            {STEPS.map((s, i) => (
              <button key={s} onClick={() => i <= step && setStep(i)} className={`text-[11px] font-medium ${i === step ? "text-[#8a6a1e]" : i < step ? "text-[#101820]/60" : "text-muted-foreground"}`}>{i + 1}. {s}</button>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-xl border border-[#C79A3B]/30 p-6 md:p-8">
          <div className="mb-5 flex items-start gap-3 bg-[#2E7D32]/10 border border-[#2E7D32]/30 rounded-lg p-4 text-sm text-[#101820]/80">
            <GraduationCap className="h-5 w-5 text-[#2E7D32] shrink-0" />
            <p>This is a student internship. <strong>No driving licence or car is required</strong> — the role is hybrid, and field activity is planned around Dublin public transport. We value potential and motivation as much as prior experience.</p>
          </div>
          <AnimatePresence mode="wait">
            <motion.div key={step} initial={{ opacity: 0, x: 12 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -12 }} transition={{ duration: 0.25 }}>

              {step === 0 && (
                <Section title="Personal Details">
                  <div className="grid sm:grid-cols-2 gap-5">
                    <TextField label="First name" required testid="wizard-input-first-name" value={profile.first_name} onChange={(v) => upd("first_name", v)} />
                    <TextField label="Last name" required testid="wizard-input-last-name" value={profile.last_name} onChange={(v) => upd("last_name", v)} />
                    <TextField label="Email" type="email" testid="wizard-input-email" value={profile.email} onChange={(v) => upd("email", v)} />
                    <TextField label="Phone" testid="wizard-input-phone" value={profile.phone} onChange={(v) => upd("phone", v)} />
                    <TextField label="Which part of Dublin are you based in?" testid="wizard-input-study-city" value={profile.study_city} onChange={(v) => upd("study_city", v)} placeholder="e.g. Dublin 2, Rathmines" />
                    <SelectField label="County" testid="wizard-input-county" options={OPT.COUNTIES} value={profile.county} onChange={(v) => upd("county", v)} />
                  </div>
                </Section>
              )}

              {step === 1 && (
                <Section title="Study & Availability">
                  <div className="grid sm:grid-cols-2 gap-5">
                    <YesNo label="Are you currently enrolled as a student for the 2026 academic year?" required testid="wizard-currently-studying" value={profile.currently_studying} onChange={(v) => upd("currently_studying", v)} />
                    <YesNo label="Do you have the right to work in Ireland (including as a student)?" required testid="wizard-right-to-work" value={profile.right_to_work} onChange={(v) => upd("right_to_work", v)} />
                    <TextField label="University / College" required testid="wizard-institution" value={profile.institution} onChange={(v) => upd("institution", v)} />
                    <TextField label="Course / Programme" required testid="wizard-course" value={profile.course} onChange={(v) => upd("course", v)} placeholder="e.g. BSc Marketing" />
                    <SelectField label="Year of study" testid="wizard-year-of-study" options={OPT.YEAR_OF_STUDY} value={profile.year_of_study} onChange={(v) => upd("year_of_study", v)} />
                    <TextField label="Expected graduation" testid="wizard-expected-graduation" value={profile.expected_graduation} onChange={(v) => upd("expected_graduation", v)} placeholder="e.g. 2027" />
                  </div>
                  <CheckGroup label="Availability (select all that apply)" testid="wizard-availability" options={OPT.INTERN_AVAILABILITY} values={profile.availability} onChange={(v) => upd("availability", v)} />
                  <CheckGroup label="Any relevant experience? (optional — potential matters more)" testid="wizard-experience" options={OPT.INTERN_EXPERIENCE} values={profile.relevant_experience} onChange={(v) => upd("relevant_experience", v)} />
                  <CheckGroup label="Tools & platforms you're comfortable with" testid="wizard-digital-tools" options={OPT.DIGITAL_TOOLS} values={profile.digital_tools} onChange={(v) => upd("digital_tools", v)} />
                </Section>
              )}

              {step === 2 && (
                <Section title="Short Answers">
                  <AreaField label="Why would you like to build your sales and marketing career with KERA?" required max={2000} testid="wizard-q-why" value={answers.q_why} onChange={(v) => updAns("q_why", v)} />
                  <AreaField label="Optional challenge: If KERA gave you one week to introduce our products to 10 new retailers in Dublin without using a car, how would you approach it?" max={2500} testid="wizard-q-challenge" value={answers.q_challenge} onChange={(v) => updAns("q_challenge", v)} />
                  <p className="text-xs text-muted-foreground">Tip: the challenge is optional, but a thoughtful answer really helps your application stand out.</p>
                </Section>
              )}

              {step === 3 && (
                <Section title="CV / Resume Upload">
                  <p className="text-sm text-muted-foreground">Upload your CV as PDF, DOC or DOCX (max 10MB). Student CVs are welcome — include your course, any part-time roles, projects and society involvement.</p>
                  {!cv ? (
                    <label data-testid="wizard-input-cv-upload" className="mt-4 flex flex-col items-center justify-center border-2 border-dashed border-[#C79A3B]/40 rounded-xl py-12 cursor-pointer hover:border-[#C79A3B] hover:bg-[#C79A3B]/5 transition-all">
                      <Upload className="h-8 w-8 text-[#C79A3B] mb-2" />
                      <span className="text-[#101820] font-medium">Click to upload your CV</span>
                      <span className="text-xs text-muted-foreground mt-1">PDF, DOC, DOCX · up to 10MB</span>
                      <input type="file" accept=".pdf,.doc,.docx" className="hidden" onChange={(e) => uploadCv(e.target.files[0])} />
                    </label>
                  ) : (
                    <div className="mt-4 bg-[#FAF6EE] border border-[#C79A3B]/30 rounded-xl p-5">
                      <div className="flex items-center gap-3">
                        <FileText className="h-8 w-8 text-[#C79A3B]" />
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-[#101820] truncate">{cv.original_filename}</div>
                          <div className="text-xs text-muted-foreground">{Math.round((cv.size || 0) / 1024)} KB</div>
                        </div>
                        <a href={`${API}/files/cv/${cv.id}/download?auth=${localStorage.getItem("kera_token")}`} target="_blank" rel="noreferrer" data-testid="cv-download-button">
                          <Button variant="outline" size="sm" className="border-[#C79A3B]/40"><Download className="h-4 w-4" /></Button>
                        </a>
                        <Button data-testid="cv-delete-button" variant="outline" size="sm" onClick={deleteCv} className="border-[#C76A32]/40 text-[#C76A32]"><Trash2 className="h-4 w-4" /></Button>
                      </div>
                      {cv.parsed_summary && (
                        <div className="mt-4 text-sm">
                          <div className="text-xs uppercase tracking-wider text-[#8a6a1e] font-semibold mb-1">CV detected summary</div>
                          <p className="text-[#101820]/75">{cv.parsed_summary}</p>
                        </div>
                      )}
                      <label className="inline-block mt-4 text-sm text-[#8a6a1e] hover:underline cursor-pointer">
                        Replace file<input type="file" accept=".pdf,.doc,.docx" className="hidden" onChange={(e) => uploadCv(e.target.files[0])} />
                      </label>
                    </div>
                  )}
                </Section>
              )}

              {step === 4 && (
                <Section title="Review & Submit">
                  <div className="space-y-5">
                    <Card title="Personal Details">
                      {rowUI("Name", `${profile.first_name || ""} ${profile.last_name || ""}`)}
                      {rowUI("Email", profile.email)}{rowUI("Phone", profile.phone)}
                      {rowUI("Based in", `${profile.study_city || ""} ${profile.county || ""}`)}
                    </Card>
                    <Card title="Study & Availability">
                      {rowUI("Currently studying", profile.currently_studying)}
                      {rowUI("Right to work", profile.right_to_work)}
                      {rowUI("University / College", profile.institution)}
                      {rowUI("Course", profile.course)}
                      {rowUI("Year of study", profile.year_of_study)}
                      {rowUI("Expected graduation", profile.expected_graduation)}
                      {rowUI("Availability", (profile.availability || []).join(", "))}
                      {rowUI("Relevant experience", (profile.relevant_experience || []).join(", "))}
                      {rowUI("Tools", (profile.digital_tools || []).join(", "))}
                    </Card>
                    <Card title="Short Answers">
                      {rowUI("Why KERA", (answers.q_why || "").slice(0, 80) + ((answers.q_why || "").length > 80 ? "…" : ""))}
                      {rowUI("Outreach challenge", (answers.q_challenge || "").slice(0, 80) + ((answers.q_challenge || "").length > 80 ? "…" : ""))}
                    </Card>
                    <Card title="CV">{rowUI("Uploaded file", cv?.original_filename)}</Card>
                  </div>
                  <div className="mt-6 space-y-3 bg-[#FAF6EE] rounded-lg border border-[#C79A3B]/25 p-4">
                    <label className="flex items-start gap-3 text-sm text-[#101820]/85">
                      <Checkbox data-testid="consent-checkbox" checked={consent} onCheckedChange={setConsent} className="mt-0.5" />
                      I consent to KERA processing the personal information provided in this application for recruitment purposes.
                    </label>
                    <label className="flex items-start gap-3 text-sm text-[#101820]/85">
                      <Checkbox data-testid="future-consent-checkbox" checked={futureConsent} onCheckedChange={setFutureConsent} className="mt-0.5" />
                      I agree that KERA may retain my profile for consideration for future suitable opportunities. (optional)
                    </label>
                  </div>
                  <Button data-testid="submit-application-button" onClick={submit} disabled={!consent}
                    className="mt-6 w-full bg-[#C79A3B] hover:bg-[#D4AF57] text-[#101820] font-bold rounded-full h-12 text-base disabled:opacity-50">
                    Submit application
                  </Button>
                </Section>
              )}

            </motion.div>
          </AnimatePresence>

          <div className="flex justify-between mt-8 pt-6 border-t border-[#C79A3B]/15">
            <Button data-testid="wizard-prev-button" variant="outline" onClick={prev} disabled={step === 0} className="border-[#C79A3B]/40 rounded-full">
              <ChevronLeft className="h-4 w-4 mr-1" /> Back
            </Button>
            {step < STEPS.length - 1 && (
              <Button data-testid="wizard-next-button" onClick={next} className="bg-[#101820] text-[#FAF6EE] rounded-full px-6">
                Save & Continue <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="space-y-5">
      <h2 className="font-heading text-xl sm:text-2xl font-bold text-[#101820]">{title}</h2>
      {children}
    </div>
  );
}
function Card({ title, children }) {
  return (
    <div className="bg-white rounded-lg border border-[#C79A3B]/25 p-4">
      <h3 className="font-heading font-semibold text-[#101820] mb-2">{title}</h3>
      {children}
    </div>
  );
}
function rowUI(label, value) {
  return (
    <div className="flex justify-between gap-4 py-1.5 border-b border-[#C79A3B]/10 text-sm">
      <span className="text-muted-foreground">{label}</span>
      <span className="text-[#101820] font-medium text-right">{value && value.trim && value.trim() ? value : (value || "—")}</span>
    </div>
  );
}
